import { IQueue, IQueueMappingSidebarItem } from "@/Settings/types/QueueMapping/QueueMapping";
import { Box, Stack, Typography } from "@mui/material";

// Layouts
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import OptionsMenuIcon from "@/assets/icons/svg/OptionsMenuIcon";
import { ReactComponent as ExclamationIcon } from "@/assets/icons/common/circled_exclamation.svg";

// Constants
const JUSTIFY_CONTENT_SPACE_BETWEEN = "space-between";
const OPTIONS_ICON_CLASSES = "side-selector-more-btn";

const getOptionMenusClasses = (disabled = false) => {
	const added = disabled ? "!text-disabled" : "!text-default";
	return `!opacity-100 !font-sans text-[16px] ${added}`;
};

const CustomTooltipComponent = (props: Readonly<{ overflowQueues: string[]; queueLabels: string[] }>) => {
	const { overflowQueues = [], queueLabels = [] } = props;

	if (!overflowQueues?.length) {
		return null;
	}

	return (
		<Stack gap={0.8}>
			<Typography className="!font-sans text-sm" component="span" mx={1}>
				Queues Overflow:
			</Typography>
			<Box>
				{overflowQueues.map((queue: string, key) => {
					const isInQueueLabels = queueLabels.includes(queue);
					const itemKey = `queue_${key}`;
					return (
						<Box key={itemKey} className="px-2">
							<Stack display={"flex"} direction="row" alignItems="center" justifyContent={JUSTIFY_CONTENT_SPACE_BETWEEN}>
								<Typography className="!font-sans text-sm" component="span">
									{queue}
								</Typography>
								{!isInQueueLabels && <ExclamationIcon className="ml-2 !text-white !fill-white" />}
							</Stack>
						</Box>
					);
				})}
			</Box>
		</Stack>
	);
};

interface IQueueMappingSidebarProps {
	queue: IQueue | null;
	queues: IQueue[] | [];
	queueLabels: string[];
	handleSelectQueue: (queue: IQueueMappingSidebarItem) => void;
	handleAddQueue: () => void;
	handleDeleteQueue: () => void;
	handleCopyQueue: () => void;
	isLoadingQueues: boolean;
	handleConfirmModalState: (state: boolean) => void;
}

const DEFAULT_FILL_COLOR_CLASS_NAME = "ml-2 fill-red-500";
const QueueMappingSidebar = (props: IQueueMappingSidebarProps) => {
	const {
		queue,
		queues,
		handleSelectQueue,
		handleAddQueue,
		handleDeleteQueue,
		handleCopyQueue,
		isLoadingQueues,
		handleConfirmModalState,
		queueLabels = [],
	} = props;

	const tree = queues.map((node) => {
		const isInQueueLabels = queueLabels.includes(node.name);
		const icon = isInQueueLabels
			? undefined
			: {
					iconNode: <ExclamationIcon className={DEFAULT_FILL_COLOR_CLASS_NAME} />,
			  };
		const overflowQueues = node?.overflowQueues || [];
		const tooltip = !overflowQueues?.length
			? undefined
			: {
					queueLabels,
					overflowQueues,
					component: <CustomTooltipComponent queueLabels={queueLabels} overflowQueues={overflowQueues} />,
			  };

		return {
			...node,
			isInQueueLabels,
			icon,
			tooltip,
		};
	});

	return (
		<AppSideBar
			searchProps={{
				searchBy: "name",
				loading: isLoadingQueues,
			}}
			sideBarProps={{
				tree,
				showBy: "name",
				height: "520px",
				selected: queue?._id ?? "",
				loading: isLoadingQueues,
				onSelectAction: handleSelectQueue,
			}}
			actions={{
				spacing: 3,
				disabled: isLoadingQueues,
				operations: [
					{
						identifier: "delete",
						classes: "btn-delete",
						color: "var(--gray-blue-color)",
						icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
						iconHover: <RemoveCircleRoundedIcon sx={{ fontSize: "35px" }} />,
						disabled: !queue,
						doubleCheckingAction: true,
						loading: isLoadingQueues,
						onClick: handleDeleteQueue,
					},
					{
						identifier: "add",
						classes: "btn-add",
						color: "var(--primary-color)",
						icon: <AddCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
						iconHover: <AddCircleRounded sx={{ fontSize: "35px" }} />,
						loading: isLoadingQueues,
						onClick: handleAddQueue,
					},
					{
						identifier: "options",
						classes: "h-[51px] w-[51px] less-opacity-disabled",
						color: "var(--gray-blue-color)",
						icon: <OptionsMenuIcon classes={OPTIONS_ICON_CLASSES} disabled={!queue} sx={{ fontSize: "29px" }} />,
						iconHover: <OptionsMenuIcon classes={OPTIONS_ICON_CLASSES} hoverVariant={true} sx={{ fontSize: "29px" }} />,
						disabled: !queue,
						loading: isLoadingQueues,
						menus: [
							{
								label: "Copy From Selected",
								disabled: !queue,
								classes: getOptionMenusClasses(!queue),
								onClick: handleCopyQueue,
							},
							{
								label: "Delete unused Queues",
								disabled: !queues?.length || !queueLabels?.length,
								classes: getOptionMenusClasses(!queues?.length),
								onClick: () => handleConfirmModalState(true),
							},
						],
					},
				],
			}}
		/>
	);
};

export default QueueMappingSidebar;
